import React, {useCallback, useEffect, useState} from "react";
import {useAtom} from "jotai";
import {useNavigate} from "react-router-dom";
import styled from "styled-components";

import Table from "../../components/table";
import {dataTotalInfo} from "../../components/common/entity";
import {columnNotice} from "./entity/NoticeEntity";
import WriteNoticeModal from "../../components/common/WriteNoticeModal";
import {selNoticeList} from "../../services/notice/NoticeAxios";
import {
  createNoticeAdmin,
  selNoticeListAdmin
} from "../../services/notice/NoticeAdminAxios";
import {tokenResultAtom} from "../login/entity/Common";

const INITIAL_CONDITION = {
  keyword: "",
  pageSize: 10,
  currentPage: 1,
  publishYn: "ALL"
};

export default function Notice() {
  const [tokenUserInfo] = useAtom(tokenResultAtom);
  const navigate = useNavigate();
  const [totalInfo, setTotalInfo] = useState(dataTotalInfo);
  const [isSearch, setIsSearch] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [searchCondition, setSearchCondition] = useState(INITIAL_CONDITION);

  const isNormalUser = tokenUserInfo.role === "NORMAL";
  const isAdmin = ["ADMIN", "SUPER_ADMIN"].includes(tokenUserInfo.role);

  useEffect(() => {
    if (tokenUserInfo.role !== "") {
      onSearch();
    }
  }, [tokenUserInfo]);

  const handleSearch = event => {
    setSearchCondition(current => ({
      ...current,
      keyword: event.target.value
    }));
  };

  const handlePublish = event => {
    setSearchCondition(current => ({
      ...current,
      publishYn: event.target.value
    }));
  };

  const handleReset = () => {
    setSearchCondition(INITIAL_CONDITION);
    setIsSearch(true);
  };

  const onWriteNotice = data => {
    if (!isAdmin) {
      return;
    }
    createNoticeAdmin(data).then(onSearch);
  };

  function onSearch() {
    setIsSearch(true);
  }

  const loadData = ({skip, limit}) => {
    const params = {
      ...searchCondition,
      currentPage: skip / limit + 1,
      pageSize: limit
    };

    if (!tokenUserInfo.role) {
      return Promise.resolve({data: [], count: 0});
    }

    if (isAdmin) {
      return selNoticeListAdmin(params).then(response => {
        const totalCount = response.totalElements;
        setIsSearch(false);
        setTotalInfo({
          totalCount,
          currentCount: response?.content.length,
          currentPage: response.pageNumber,
          totalPages: response.totalPages
        });
        return {data: response.content, count: parseInt(totalCount)};
      });
    }

    if (isNormalUser) {
      return selNoticeList({...params, publishYn: "Y"}).then(response => {
      const rows = Array.isArray(response?.rows)
          ? response.rows
          : Array.isArray(response?.content)
              ? response.content
              : [];
      const totalCount = response?.totalCount ?? response?.totalElements ?? rows.length;
      setIsSearch(false);
      setTotalInfo({
        totalCount,
        currentCount: rows.length,
        currentPage: response?.currentPage ?? response?.pageNumber,
        totalPages: response?.totalPages
      });
      return {data: rows, count: Number(totalCount)};
      });
    }

    return Promise.resolve({data: [], count: 0});
  };

  const dataSource = useCallback(loadData, [
    searchCondition.keyword,
    searchCondition.publishYn,
    searchCondition.pageSize,
    isSearch,
    tokenUserInfo.role
  ]);

  const publishLabel = searchCondition.publishYn === "Y"
      ? "게시"
      : searchCondition.publishYn === "N"
          ? "미게시"
          : "전체";

  const handleNoticeRowClick = ({data}) => {
    if (!data) {
      return;
    }

    navigate("/board/noticeDetail", {
      state: {data}
    });
  };

  return (
      <NoticePage>
        <ConditionCard>
          <ConditionToggleButton
              type="button"
              aria-expanded={isOpen}
              aria-controls="notice-condition-body"
              onClick={() => setIsOpen(open => !open)}
          >
            <HeaderTitleArea>
              <HeaderAccent />
              <div>
                <HeaderTitle>조회 조건</HeaderTitle>
                <HeaderDescription>
                  공지사항 제목과 게시 여부를 기준으로 목록을 조회합니다.
                </HeaderDescription>
              </div>
            </HeaderTitleArea>

            <ConditionHeaderRight>
              {!isOpen && (
                  <ConditionSummary>
                    {isAdmin && <SummaryChip>{publishLabel}</SummaryChip>}
                    {searchCondition.keyword && (
                        <SummaryChip $accent>{searchCondition.keyword}</SummaryChip>
                    )}
                  </ConditionSummary>
              )}
              <DropdownIcon $open={isOpen} aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24">
                  <path fill="currentColor" d="m7 10 5 5 5-5H7Z" />
                </svg>
              </DropdownIcon>
            </ConditionHeaderRight>
          </ConditionToggleButton>

          {isOpen && (
              <ConditionBody id="notice-condition-body">
                <FilterGrid>
                  <FilterItem>
                    <FilterLabel>공지사항 검색</FilterLabel>
                    <KeywordInputWrap>
                      <SearchIcon aria-hidden="true">
                        <svg width="15" height="15" viewBox="0 0 24 24">
                          <path
                              fill="currentColor"
                              d="m21 19-4.35-4.35a7.5 7.5 0 1 0-2 2L19 21l2-2ZM5 10.5a5.5 5.5 0 1 1 11 0 5.5 5.5 0 0 1-11 0Z"
                          />
                        </svg>
                      </SearchIcon>
                      <KeywordInput
                          type="text"
                          placeholder="공지사항 제목 검색"
                          value={searchCondition.keyword}
                          onChange={handleSearch}
                          onKeyDown={event => {
                            if (event.key === "Enter") {
                              event.preventDefault();
                              onSearch();
                            }
                          }}
                      />
                    </KeywordInputWrap>
                  </FilterItem>

                  {isAdmin && (
                      <FilterItem $compact>
                        <FilterLabel>게시 여부</FilterLabel>
                        <PublishSelect
                            value={searchCondition.publishYn}
                            onChange={handlePublish}
                        >
                          <option value="ALL">전체</option>
                          <option value="Y">게시</option>
                          <option value="N">미게시</option>
                        </PublishSelect>
                      </FilterItem>
                  )}
                </FilterGrid>

                <ConditionFooter>
                  <ConditionGuide>
                    조건을 설정한 뒤 적용 버튼을 눌러 공지사항 목록을 조회합니다.
                  </ConditionGuide>
                  <ConditionActions>
                    <ResetButton type="button" onClick={handleReset}>
                      초기화
                    </ResetButton>
                    <ApplyButton type="button" onClick={onSearch}>
                      적용
                    </ApplyButton>
                  </ConditionActions>
                </ConditionFooter>
              </ConditionBody>
          )}
        </ConditionCard>

        <ResultCard>
          <ResultHeader>
            <HeaderTitleArea>
              <HeaderAccent />
              <div>
                <ResultTitleLine>
                  <HeaderTitle>공지사항 목록</HeaderTitle>
                  <ResultCount>
                    <strong>{totalInfo.totalCount ?? 0}</strong>
                    <span>건</span>
                  </ResultCount>
                </ResultTitleLine>
                <HeaderDescription>
                  등록된 공지사항과 게시 상태를 확인합니다.
                </HeaderDescription>
              </div>
            </HeaderTitleArea>

            {isAdmin && (
                <AddNoticeAction>
                  <WriteNoticeModal
                      formType="notice"
                      onClick={onWriteNotice}
                      title="공지사항 작성"
                      buttonText="+ 공지사항 추가"
                  />
                </AddNoticeAction>
            )}
          </ResultHeader>

          <ResultBody>
            <TableFrame>
              <Table
                  columns={isAdmin
                      ? columnNotice
                      : columnNotice.filter(column => column.name !== "publishYn")}
                  defaultLimit={searchCondition.pageSize}
                  data={dataSource}
                  onRowClick={handleNoticeRowClick}
              />
            </TableFrame>
          </ResultBody>
        </ResultCard>
      </NoticePage>
  );
}

const NoticePage = styled.div`
  display: flex;
  width: 100%;
  box-sizing: border-box;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
`;

const ConditionCard = styled.section`
  overflow: visible;
  border: 1px solid #e5e8eb;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 5px 18px rgba(31, 41, 55, 0.045);
`;

const ConditionToggleButton = styled.button`
  display: flex;
  width: 100%;
  min-height: 74px;
  box-sizing: border-box;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 16px 20px;
  border: 0;
  border-radius: 10px 10px 0 0;
  background: #fff;
  text-align: left;
  cursor: pointer;
`;

const HeaderTitleArea = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 11px;
`;

const HeaderAccent = styled.span`
  width: 4px;
  height: 34px;
  flex: 0 0 4px;
  border-radius: 2px;
  background: #f5811f;
`;

const HeaderTitle = styled.div`
  color: #222;
  font-size: 17px;
  font-weight: 700;
`;

const HeaderDescription = styled.div`
  margin-top: 5px;
  color: #888;
  font-size: 12px;
  line-height: 1.5;
`;

const ConditionHeaderRight = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 12px;
`;

const ConditionSummary = styled.div`
  display: flex;
  min-width: 0;
  justify-content: flex-end;
  gap: 6px;
`;

const SummaryChip = styled.span`
  max-width: 180px;
  overflow: hidden;
  padding: 5px 9px;
  border: 1px solid ${({$accent}) => $accent ? "#f1d4c2" : "#e4e7ea"};
  border-radius: 13px;
  background: ${({$accent}) => $accent ? "#fff4e8" : "#f8f9fa"};
  color: ${({$accent}) => $accent ? "#d96500" : "#777"};
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DropdownIcon = styled.span`
  display: inline-flex;
  color: #f5811f;
  transform: rotate(${({$open}) => $open ? "180deg" : "0deg"});
  transition: transform 0.18s ease;
`;

const ConditionBody = styled.div`
  padding: 18px 20px 20px;
  border-top: 1px solid #eee;
  background: #fff;
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(300px, 1fr) minmax(160px, 240px);
  gap: 16px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const FilterItem = styled.div`
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 7px;
`;

const FilterLabel = styled.label`
  color: #555;
  font-size: 13px;
  font-weight: 700;
`;

const KeywordInputWrap = styled.div`
  position: relative;
  width: 100%;
`;

const SearchIcon = styled.span`
  position: absolute;
  top: 50%;
  left: 13px;
  display: inline-flex;
  color: #999;
  pointer-events: none;
  transform: translateY(-50%);
`;

const KeywordInput = styled.input`
  width: 100%;
  height: 38px;
  box-sizing: border-box;
  padding: 0 13px 0 38px;
  border: 1px solid #dfe3e7;
  border-radius: 6px;
  background: #fff;
  color: #444;
  font-size: 13px;
  outline: none;

  &:focus {
    border-color: #f5811f;
  }
`;

const PublishSelect = styled.select`
  width: 100%;
  height: 38px;
  box-sizing: border-box;
  padding: 0 34px 0 12px;
  border: 1px solid #dfe3e7;
  border-radius: 6px;
  background: #fff;
  color: #444;
  font-size: 13px;
  outline: none;

  &:focus {
    border-color: #f5811f;
  }
`;

const ConditionFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 16px;
`;

const ConditionGuide = styled.div`
  color: #999;
  font-size: 12px;
`;

const ConditionActions = styled.div`
  display: flex;
  gap: 8px;
`;

const ResetButton = styled.button`
  min-width: 72px;
  height: 36px;
  border: 1px solid #dfe3e7;
  border-radius: 6px;
  background: #fff;
  color: #666;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
`;

const ApplyButton = styled.button`
  min-width: 72px;
  height: 36px;
  border: 1px solid #f5811f;
  border-radius: 6px;
  background: #fff4e8;
  color: #f5811f;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    background: #ffe8d2;
  }
`;

const ResultCard = styled.section`
  overflow: hidden;
  border: 1px solid #e5e8eb;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 5px 18px rgba(31, 41, 55, 0.045);
`;

const ResultHeader = styled.header`
  display: flex;
  min-height: 74px;
  box-sizing: border-box;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
  background: #f9fafb;
`;

const ResultTitleLine = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ResultCount = styled.div`
  display: inline-flex;
  min-height: 25px;
  align-items: center;
  gap: 4px;
  padding: 0 9px;
  border: 1px solid #f1d4c2;
  border-radius: 13px;
  background: #fff4e8;

  strong { color: #f5811f; font-size: 13px; }
  span { color: #888; font-size: 11px; }
`;

const AddNoticeAction = styled.div`
  button {
    width: auto;
    min-width: 112px;
    height: 34px;
    margin: 0;
    padding: 0 14px;
    border: 1px solid #f5811f;
    border-radius: 6px;
    background: #f5811f;
    color: #fff;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
  }

  button:hover {
    background: #df6f10;
    color: #fff;
  }
`;

const ResultBody = styled.div`
  padding: 14px 20px 20px;
`;

const TableFrame = styled.div`
  overflow: hidden;
  background: #fff;

  & > div,
  & > section,
  & > section > div:last-child {
    margin: 0;
    border: 0 !important;
    border-radius: 0 !important;
    box-shadow: none !important;
  }

  .InovuaReactDataGrid,
  .inovua-react-toolkit-table {
    border: 1px solid #e5e8eb !important;
    border-radius: 8px !important;
    box-shadow: none !important;
  }

  .InovuaReactDataGrid__row {
    cursor: pointer;
  }
`;
